// src/pages/Appointments/Admin/AdminAppointmentDashboard_Enhanced.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiCalendar,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiTrendingUp,
  FiDollarSign,
  FiUsers,
  FiAlertCircle,
  FiRefreshCw,
  FiEye,
} from "react-icons/fi";
import { getDashboardStatisticsThunk } from "../../../store/thunks/appointmentThunks";
import Button from "../../../components/common/Button";
import { setSelectedBranch } from "store/slices/branchSlice";

const AdminAppointmentDashboardEnhanced = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { dashboardStats, loadingStats, statsError } = useSelector(
    (state) => state.appointment
  );
  const { selectedBranch, branches } = useSelector((state) => state.branch);

  const [dateRange, setDateRange] = useState("30"); // days
  const [selectedBranchFilter, setSelectedBranchFilter] = useState("");

  useEffect(() => {
    loadDashboardStats();
  }, [selectedBranch, dateRange, selectedBranchFilter]);

  const loadDashboardStats = () => {
    const params = {
      branchCode: selectedBranchFilter || selectedBranch,
    };

    // Calculate date range
    if (dateRange !== "all") {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));
      params.startDate = startDate.toISOString();
      params.endDate = endDate.toISOString();
    }

    dispatch(getDashboardStatisticsThunk(params));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loadingStats && !dashboardStats) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (statsError) {
    return (
      <div className="container mx-auto p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <FiAlertCircle className="mx-auto text-4xl text-red-500 mb-2" />
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Error Loading Statistics
          </h3>
          <p className="text-red-600 mb-4">{statsError}</p>
          <Button onClick={loadDashboardStats}>
            <FiRefreshCw className="mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const stats = dashboardStats || {};

  const handleBranchChange = (branchCode) => {
    dispatch(setSelectedBranch(branchCode));
    setSelectedBranchFilter(branchCode);
    // Appointments will auto-reload due to useEffect dependency on selectedBranch
  };

  return (
    <div className="container mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-text mb-1">
              Appointment Dashboard
            </h1>
            <p className="text-neutral-dark">
              Comprehensive overview of your appointment system
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={loadDashboardStats}
              disabled={loadingStats}
            >
              <FiRefreshCw
                className={`mr-2 ${loadingStats ? "animate-spin" : ""}`}
              />
              Refresh
            </Button>
            <Button onClick={() => navigate("/appointments/calendar")}>
              <FiCalendar className="mr-2" />
              Calendar View
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Date Range Filter */}
          <div>
            <label className="block text-sm font-medium text-text mb-2">
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full px-4 py-2 border border-neutral-light rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="60">Last 60 days</option>
              <option value="90">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>

          {/* Branch Filter */}
          <div>
            <label className="block text-sm font-medium text-text mb-2">
              Branch
            </label>
            <select
              value={selectedBranchFilter}
              onChange={(e) => handleBranchChange(e.target.value)}
              className="w-full px-4 py-2 border border-neutral-light rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Current Branch</option>
              <option value="all">All Branches</option>
              {branches?.map((branch) => (
                <option key={branch.code} value={branch.code}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Today's Overview */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-text mb-4">
          Today's Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <StatCard
            icon={<FiCalendar />}
            label="Total Today"
            value={stats.today?.total || 0}
            color="blue"
            onClick={() => navigate("/appointments/today")}
          />
          <StatCard
            icon={<FiClock />}
            label="Pending"
            value={stats.today?.pending || 0}
            color="yellow"
            onClick={() => navigate("/appointments/pending")}
          />
          <StatCard
            icon={<FiCheckCircle />}
            label="Confirmed"
            value={stats.today?.confirmed || 0}
            color="green"
          />
          <StatCard
            icon={<FiUsers />}
            label="Checked In"
            value={stats.today?.checkedIn || 0}
            color="purple"
            onClick={() => navigate("/appointments/active-visits")}
          />
          <StatCard
            icon={<FiCheckCircle />}
            label="Completed"
            value={stats.today?.completed || 0}
            color="gray"
          />
        </div>
      </div>

      {/* Key Metrics */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-text mb-4">Key Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <MetricCard
            icon={<FiCalendar />}
            label="Total Appointments"
            value={stats.overview?.totalAppointments || 0}
            color="blue"
          />
          <MetricCard
            icon={<FiTrendingUp />}
            label="Conversion Rate"
            value={`${stats.performance?.conversionRate || 0}%`}
            subValue={`${
              stats.performance?.convertedAppointments || 0
            } converted`}
            color="green"
          />
          <MetricCard
            icon={<FiDollarSign />}
            label="Total Revenue"
            value={formatCurrency(stats.performance?.totalRevenue || 0)}
            subValue={`Avg: ${formatCurrency(
              stats.performance?.averageOrderValue || 0
            )}`}
            color="green"
          />
          <MetricCard
            icon={<FiClock />}
            label="Avg Visit Duration"
            value={`${stats.metrics?.averageVisitDuration || 0} min`}
            color="purple"
          />
        </div>
      </div>

      {/* Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Status Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-text mb-4 flex items-center">
            <FiCalendar className="mr-2" />
            Status Breakdown
          </h3>
          <div className="space-y-3">
            {Object.entries(stats.overview?.statusBreakdown || {}).map(
              ([status, count]) => (
                <StatusBar
                  key={status}
                  status={status}
                  count={count}
                  total={stats.overview?.totalAppointments || 1}
                />
              )
            )}
          </div>
        </div>

        {/* Alerts & Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-text mb-4 flex items-center">
            <FiAlertCircle className="mr-2" />
            Alerts & Actions
          </h3>
          <div className="space-y-3">
            <AlertItem
              icon={<FiClock />}
              label="Pending Approvals"
              count={stats.overview?.pendingApprovals || 0}
              action={() => navigate("/appointments/pending")}
              color="yellow"
            />
            <AlertItem
              icon={<FiCalendar />}
              label="Upcoming (7 days)"
              count={stats.upcoming?.next7Days || 0}
              action={() => navigate("/appointments/upcoming")}
              color="blue"
            />
            <AlertItem
              icon={<FiXCircle />}
              label="Cancellation Rate"
              count={`${stats.metrics?.cancellationRate || 0}%`}
              color={
                parseFloat(stats.metrics?.cancellationRate || 0) > 20
                  ? "red"
                  : "green"
              }
            />
            <AlertItem
              icon={<FiAlertCircle />}
              label="No-Show Rate"
              count={`${stats.metrics?.noShowRate || 0}%`}
              color={
                parseFloat(stats.metrics?.noShowRate || 0) > 10
                  ? "red"
                  : "green"
              }
            />
          </div>
        </div>

        {/* Top Appointment Types */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-text mb-4 flex items-center">
            <FiTrendingUp className="mr-2" />
            Top Appointment Types
          </h3>
          <div className="space-y-3">
            {stats.breakdown?.byType?.slice(0, 5).map((type) => (
              <div key={type._id} className="flex items-center justify-between">
                <span className="text-sm text-neutral-dark truncate flex-1">
                  {type._id.replace(/_/g, " ")}
                </span>
                <span className="text-sm font-semibold text-text ml-2">
                  {type.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Daily Trend Chart */}
      {stats.trends?.daily && stats.trends.daily.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold text-text mb-4">
            Last 7 Days Trend
          </h3>
          <div className="space-y-2">
            {stats.trends.daily.map((day) => (
              <DayTrendBar
                key={day._id}
                date={day._id}
                total={day.count}
                completed={day.completed}
                cancelled={day.cancelled}
              />
            ))}
          </div>
        </div>
      )}

      {/* Admin Performance (if available) */}
      {stats.adminPerformance && stats.adminPerformance.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-text mb-4 flex items-center">
            <FiUsers className="mr-2" />
            Admin Performance
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-light">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-text">
                    Admin
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-text">
                    Appointments
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-text">
                    Completed
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-text">
                    Conversion Rate
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-text">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.adminPerformance.slice(0, 10).map((admin) => (
                  <tr
                    key={admin.adminCode}
                    className="border-b border-neutral-light"
                  >
                    <td className="py-3 px-4 text-sm text-text">
                      {admin.adminName}
                    </td>
                    <td className="text-center py-3 px-4 text-sm text-neutral-dark">
                      {admin.totalAppointments}
                    </td>
                    <td className="text-center py-3 px-4 text-sm text-neutral-dark">
                      {admin.completed}
                    </td>
                    <td className="text-center py-3 px-4 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          parseFloat(admin.conversionRate) >= 50
                            ? "bg-green-100 text-green-800"
                            : parseFloat(admin.conversionRate) >= 30
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {admin.conversionRate}%
                      </span>
                    </td>
                    <td className="text-right py-3 px-4 text-sm font-semibold text-text">
                      {formatCurrency(admin.totalRevenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h3 className="text-lg font-semibold text-text mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/appointments/list")}
            className="w-full"
          >
            <FiEye className="mr-2" />
            View All
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/appointments/calendar")}
            className="w-full"
          >
            <FiCalendar className="mr-2" />
            Calendar
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/appointments/analytics")}
            className="w-full"
          >
            <FiTrendingUp className="mr-2" />
            Analytics
          </Button>
          <Button
            onClick={() => navigate("/appointments/book")}
            className="w-full"
          >
            <FiCalendar className="mr-2" />
            Book New
          </Button>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, color, onClick }) => {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    yellow: "bg-yellow-100 text-yellow-600",
    purple: "bg-purple-100 text-purple-600",
    gray: "bg-gray-100 text-gray-600",
  };

  return (
    <div
      className={`bg-white rounded-lg shadow p-4 ${
        onClick ? "cursor-pointer hover:shadow-lg" : ""
      }`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-sm text-neutral-dark">{label}</p>
          <p className="text-2xl font-bold text-text">{value}</p>
        </div>
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ icon, label, value, subValue, color }) => {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    purple: "bg-purple-100 text-purple-600",
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-start justify-between mb-3">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>{icon}</div>
      </div>
      <p className="text-sm text-neutral-dark mb-1">{label}</p>
      <p className="text-3xl font-bold text-text mb-1">{value}</p>
      {subValue && <p className="text-xs text-neutral-dark">{subValue}</p>}
    </div>
  );
};

// Status Bar Component
const StatusBar = ({ status, count, total }) => {
  const percentage = ((count / total) * 100).toFixed(0);

  const statusConfig = {
    PENDING: { label: "Pending", color: "bg-yellow-500" },
    CONFIRMED: { label: "Confirmed", color: "bg-green-500" },
    CHECKED_IN: { label: "Checked In", color: "bg-blue-500" },
    IN_PROGRESS: { label: "In Progress", color: "bg-purple-500" },
    COMPLETED: { label: "Completed", color: "bg-gray-500" },
    CANCELLED: { label: "Cancelled", color: "bg-red-500" },
    NO_SHOW: { label: "No Show", color: "bg-orange-500" },
    RESCHEDULED: { label: "Rescheduled", color: "bg-indigo-500" },
  };

  const config = statusConfig[status] || {
    label: status,
    color: "bg-gray-500",
  };

  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-neutral-dark">{config.label}</span>
        <span className="font-semibold text-text">
          {count} ({percentage}%)
        </span>
      </div>
      <div className="w-full bg-neutral-light rounded-full h-2">
        <div
          className={`${config.color} h-2 rounded-full`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Alert Item Component
const AlertItem = ({ icon, label, count, action, color }) => {
  const colorClasses = {
    blue: "text-blue-600",
    green: "text-green-600",
    yellow: "text-yellow-600",
    red: "text-red-600",
  };

  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg bg-neutral-light ${
        action ? "cursor-pointer hover:bg-neutral-200" : ""
      }`}
      onClick={action}
    >
      <div className="flex items-center gap-2">
        <div className={colorClasses[color]}>{icon}</div>
        <span className="text-sm text-text">{label}</span>
      </div>
      <span className={`font-semibold ${colorClasses[color]}`}>{count}</span>
    </div>
  );
};

// Day Trend Bar Component
const DayTrendBar = ({ date, total, completed, cancelled }) => {
  const completedPercentage = ((completed / total) * 100).toFixed(0);
  const cancelledPercentage = ((cancelled / total) * 100).toFixed(0);

  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-neutral-dark">
          {new Date(date).toLocaleDateString("en-IN", {
            month: "short",
            day: "numeric",
          })}
        </span>
        <span className="text-text font-semibold">{total} appointments</span>
      </div>
      <div className="w-full bg-neutral-light rounded-full h-3 overflow-hidden flex">
        <div
          className="bg-green-500 h-3"
          style={{ width: `${completedPercentage}%` }}
          title={`${completed} completed`}
        />
        <div
          className="bg-red-500 h-3"
          style={{ width: `${cancelledPercentage}%` }}
          title={`${cancelled} cancelled`}
        />
      </div>
    </div>
  );
};

export default AdminAppointmentDashboardEnhanced;
